// Currency formatting utilities

export interface CurrencySymbol {
  [key: string]: string;
}

export const CURRENCY_SYMBOLS: CurrencySymbol = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'C$',
  AUD: 'A$',
  JPY: '¥',
  KRW: '₩',
  CHF: 'CHF',
  CNY: '¥',
  INR: '₹',
  BRL: 'R$',
  MXN: 'MXN$',
  RUB: '₽',
  ZAR: 'R',
  SGD: 'S$',
  HKD: 'HK$',
  NOK: 'kr',
  SEK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  AED: 'AED ',
};

export const ZERO_DECIMAL_CURRENCIES = [
  'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW',
  'MGA', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
];

/**
 * Formats a currency amount with the appropriate symbol and decimal places
 * @param amount - The amount to format (number or string)
 * @param currency - The currency code (e.g., 'USD', 'EUR')
 * @returns Formatted currency string (e.g., '$25.00', '¥2500')
 */
export function formatCurrency(amount: number | string, currency: string): string {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) {
    return `${currency} 0`;
  }

  const currencyCode = currency.toUpperCase();
  const symbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode;

  // For currencies that don't use decimal places
  const decimalPlaces = ZERO_DECIMAL_CURRENCIES.includes(currencyCode) ? 0 : 2;

  return `${symbol}${numAmount.toFixed(decimalPlaces)}`;
}

/**
 * Gets the currency symbol for a given currency code
 * @param currency - The currency code (e.g., 'USD', 'EUR')
 * @returns The currency symbol or the currency code if symbol not found
 */
export function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency.toUpperCase()] || currency.toUpperCase();
}

/**
 * Checks if a currency uses decimal places
 * @param currency - The currency code (e.g., 'USD', 'JPY')
 * @returns true if currency uses decimals, false otherwise
 */
export function currencyUsesDecimals(currency: string): boolean {
  return !ZERO_DECIMAL_CURRENCIES.includes(currency.toUpperCase());
}

/** Convert minor units (e.g. cents) to major units (e.g. dollars). For zero-decimal currencies, returns the value unchanged. */
export function minorToMajor(amount: number, currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.includes(currency.toUpperCase()) ? amount : amount / 100;
}
